import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useEffect, useEffectEvent, useRef } from 'react';
import { NavItem } from '../../../helpers';
import MobileMenuList from './MobileMenuList';

interface MobileNavProps {
  items: NavItem[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const BAR_CLASS = 'absolute h-0.5 w-6 rounded-full bg-current';

const HamburgerIcon = ({ open, duration }: { open: boolean; duration: number }) => (
  <span className="relative flex h-7 w-7 items-center justify-center" aria-hidden="true">
    <m.span
      className={BAR_CLASS}
      animate={{ y: open ? 0 : -7, rotate: open ? 45 : 0 }}
      transition={{ duration }}
    />
    <m.span className={BAR_CLASS} animate={{ opacity: open ? 0 : 1 }} transition={{ duration }} />
    <m.span
      className={BAR_CLASS}
      animate={{ y: open ? 0 : 7, rotate: open ? -45 : 0 }}
      transition={{ duration }}
    />
  </span>
);

const MobileNav = ({ items, open, onToggle, onClose }: MobileNavProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const duration = reducedMotion ? 0 : 0.2;
  const onCloseEvent = useEffectEvent(onClose);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseEvent();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Focus trap
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const focusable = Array.from(menuRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    focusable[0]?.focus();
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [open]);

  return (
    <div className="flex lg:hidden items-center">
      <button
        type="button"
        onClick={onToggle}
        aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={open}
        aria-controls="mobile-menu"
        className="p-2 text-inherit rounded-sm focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
      >
        <HamburgerIcon open={open} duration={duration} />
      </button>
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop — below header (z-40) so clicking outside closes the menu */}
            <m.div
              className="fixed inset-0 z-40"
              onClick={onClose}
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration }}
            />
            <m.div
              id="mobile-menu"
              ref={menuRef}
              className="absolute top-full left-0 right-0 bg-primary overflow-hidden z-50 origin-top"
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration }}
            >
              <MobileMenuList items={items} onClose={onClose} />
            </m.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileNav;
